import React, { useState, useMemo } from "react";
import { Helmet } from "react-helmet";
import Main from "../../layout/Main";
import { Link } from "react-router-dom";

const termsSections = [
  {
    id: "section-1",
    num: "1",
    title: "Introduction",
    content: (
      <div className="space-y-4">
        <p>Welcome to Entrusted Limited (&quot;Entrusted&quot;, &quot;we&quot;, &quot;us&quot; or &quot;our&quot;).</p>
        <p>
          These Terms of Use (&quot;Terms&quot;) govern your access to and use of the Entrusted digital platform, website, mobile application and related services (collectively, the &quot;Platform&quot; or &quot;Services&quot;).
        </p>
        <p>
          Entrusted provides inbound international money remittance services that enable eligible customers to send money to beneficiaries in Nigeria, subject to applicable laws, CBN regulatory requirements, transaction limits, verification requirements and these Terms.
        </p>
        <p>
          By registering for an account, accessing or using the Platform, you confirm that you have read, understood and agree to be bound by these Terms. If you do not agree with these Terms, you must not use the Platform.
        </p>
      </div>
    ),
  },
  {
    id: "section-2",
    num: "2",
    title: "Eligibility",
    content: (
      <div className="space-y-4">
        <p>To use Entrusted&apos;s Services, you must:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>Be legally capable of entering into a binding agreement under the laws of your country of residence and Nigeria</li>
          <li>Provide accurate, complete and current information</li>
          <li>Satisfy Entrusted&apos;s applicable Know Your Customer (KYC) and verification requirements</li>
          <li>Meet any applicable age, residency, nationality or regulatory requirements</li>
          <li>Not be subject to applicable sanctions, restrictions or prohibitions</li>
          <li>Use the Platform only for lawful purposes</li>
        </ul>
        <p>
          Entrusted reserves the right to refuse registration, restrict access or terminate an account where regulatory, compliance, security or other legitimate concerns exist.
        </p>
      </div>
    ),
  },
  {
    id: "section-3",
    num: "3",
    title: "Registration and Account",
    content: (
      <div className="space-y-4">
        <p>To access certain Services, you may be required to create an Entrusted account. You agree to:</p>
        <ol className="list-decimal pl-5 space-y-2">
          <li>Provide truthful and accurate information</li>
          <li>Keep your personal and account information up to date</li>
          <li>Protect your login credentials and authentication information</li>
          <li>Immediately notify Entrusted if you suspect unauthorised access to your account</li>
          <li>Not allow another person to use your account</li>
          <li>Not create an account using false, misleading or stolen information</li>
        </ol>
        <p>
          You are responsible for all activities conducted through your account unless such activity resulted from Entrusted&apos;s proven failure to maintain appropriate security controls. Entrusted may suspend or restrict an account where there is suspected fraud, unauthorised activity, regulatory concern or violation of these Terms.
        </p>
      </div>
    ),
  },
  {
    id: "section-4",
    num: "4",
    title: "Identity Verification and KYC",
    content: (
      <div className="space-y-4">
        <p>
          Entrusted is required to comply with applicable laws and regulatory requirements relating to customer identification, KYC, anti-money laundering, counter-terrorist financing, counter-proliferation financing and sanctions compliance.
        </p>
        <p>You may therefore be required to provide information or documents such as:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>Full name, date of birth, residential address, telephone number and email address</li>
          <li>Identification documents (passport, national ID or other government-issued ID)</li>
          <li>National Identification Number (NIN) and/or Bank Verification Number (BVN)</li>
          <li>Bank account information</li>
          <li>Source-of-funds information</li>
          <li>Any other information reasonably required for verification or regulatory compliance</li>
        </ul>
        <p>
          Entrusted may use approved third-party verification services to validate information provided by you. Failure to provide satisfactory information may result in the rejection, suspension or cancellation of a transaction or account.
        </p>
      </div>
    ),
  },
  {
    id: "section-5",
    num: "5",
    title: "Remittance Services",
    content: (
      <div className="space-y-4">
        <p>
          Entrusted enables eligible customers to initiate inbound international money transfer transactions to beneficiaries in Nigeria.
        </p>
        <p>A transaction may require:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>Sender verification and KYC clearance</li>
          <li>Beneficiary information and bank account details</li>
          <li>Transaction amount, applicable exchange rate and fees</li>
          <li>Source-of-funds and purpose-of-transaction information</li>
          <li>Any other information required for regulatory or operational purposes</li>
        </ul>
        <p>
          Entrusted may decline, delay, suspend or cancel a transaction where necessary to comply with applicable laws, CBN requirements, internal risk controls, sanctions obligations or these Terms. Payouts are made in Naira through authorised channels in accordance with CBN directives.
        </p>
      </div>
    ),
  },
  {
    id: "section-6",
    num: "6",
    title: "Transaction Instructions",
    content: (
      <div className="space-y-4">
        <p>You are solely responsible for ensuring that all transaction information submitted through the Platform is accurate. This includes:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>Beneficiary name and bank details</li>
          <li>Account number</li>
          <li>Transaction amount and purpose</li>
          <li>Any other information requested by Entrusted</li>
        </ul>
        <p>
          Where incorrect information is supplied by you, Entrusted shall not be responsible for losses resulting from such errors, subject only to mandatory provisions of applicable law. Once a transaction has been submitted and processing or payout has commenced, Entrusted may be unable to modify or cancel it.
        </p>
      </div>
    ),
  },
  {
    id: "section-7",
    num: "7",
    title: "Exchange Rates",
    content: (
      <div className="space-y-4">
        <p>
          Where applicable, Entrusted will display the applicable exchange rate before you confirm a transaction. Exchange rates may change based on market conditions and applicable regulatory requirements (including CBN directives on the use of prevailing rates).
        </p>
        <p>
          The exchange rate applicable to a transaction will generally be the rate displayed and accepted by you at the point of transaction confirmation, subject to any disclosed conditions. Entrusted does not guarantee that the exchange rate offered will remain available after the transaction has been confirmed.
        </p>
      </div>
    ),
  },
  {
    id: "section-8",
    num: "8",
    title: "Fees and Charges",
    content: (
      <div className="space-y-4">
        <p>
          Entrusted may charge fees for certain Services. Before confirming a transaction, applicable fees and charges will be presented to you where required by law or CBN guidelines. You authorise Entrusted to deduct applicable fees from the amount payable or otherwise collect such fees in accordance with the transaction terms.
        </p>
        <p>
          Additional charges imposed by third-party financial institutions, banks, payment providers or other service providers may apply where disclosed or permitted. Entrusted will transparently disclose applicable exchange rates, commissions, fees and other charges as required under the CBN IMTO Guidelines.
        </p>
      </div>
    ),
  },
  {
    id: "section-9",
    num: "9",
    title: "Payments and Funding",
    content: (
      <div className="space-y-4">
        <p>Where applicable, customers may fund transactions through payment methods supported by Entrusted and its authorised payment partners. You confirm that:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>You are authorised to use the payment method</li>
          <li>The funds are from lawful sources</li>
          <li>You will not use stolen, fraudulent or unauthorised payment instruments</li>
          <li>You will provide information reasonably required to verify the transaction</li>
        </ul>
        <p>
          Entrusted may reject or reverse a transaction where the funding method is suspected to be fraudulent, unauthorised or otherwise unlawful.
        </p>
      </div>
    ),
  },
  {
    id: "section-10",
    num: "10",
    title: "Beneficiary and Payout",
    content: (
      <div className="space-y-4">
        <p>
          Where a transaction is successfully processed, Entrusted will facilitate payout to the designated beneficiary through an authorised payout channel (typically an eligible Nigerian bank account or other supported method).
        </p>
        <p>Payout is subject to:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>Successful transaction processing and compliance clearance</li>
          <li>Beneficiary verification where required</li>
          <li>Availability of the payout channel</li>
          <li>Applicable transaction limits</li>
          <li>Regulatory requirements and CBN directives</li>
        </ul>
        <p>
          Entrusted is not responsible for delays caused by circumstances outside its reasonable control, including failures or delays involving third-party banks, payment providers, telecommunications networks or other infrastructure.
        </p>
      </div>
    ),
  },
  {
    id: "section-11",
    num: "11",
    title: "Transaction Limits",
    content: (
      <div className="space-y-4">
        <p>Entrusted may impose transaction, daily, monthly or other limits. These limits may be determined by:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>Applicable Nigerian regulations and CBN directives</li>
          <li>Customer verification level (KYC tier)</li>
          <li>Transaction type, source and destination</li>
          <li>Risk assessment</li>
          <li>Payment or payout partner requirements</li>
          <li>Entrusted&apos;s internal policies</li>
        </ul>
        <p>
          Entrusted may change applicable limits where permitted by law and will notify customers of material changes where required.
        </p>
      </div>
    ),
  },
  {
    id: "section-12",
    num: "12",
    title: "Prohibited Activities",
    content: (
      <div className="space-y-4">
        <p>You must not use the Platform to:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>Conduct fraudulent transactions</li>
          <li>Launder money or finance terrorism or other unlawful activities</li>
          <li>Facilitate sanctions evasion</li>
          <li>Transfer proceeds of crime</li>
          <li>Use stolen or unauthorised payment instruments</li>
          <li>Provide false or misleading information</li>
          <li>Impersonate another person</li>
          <li>Circumvent transaction limits or verification controls</li>
          <li>Conduct transactions on behalf of undisclosed third parties where prohibited</li>
          <li>Engage in illegal trade or activities</li>
          <li>Attempt to compromise or interfere with the security of the Platform</li>
        </ul>
        <p>
          Entrusted may report suspected unlawful activity to appropriate regulatory, law-enforcement or government authorities where required or permitted by law.
        </p>
      </div>
    ),
  },
  {
    id: "section-13",
    num: "13",
    title: "Anti-Money Laundering and Sanctions Compliance",
    content: (
      <div className="space-y-4">
        <p>
          Entrusted maintains controls designed to comply with applicable Anti-Money Laundering (AML), Counter-Terrorist Financing (CTF), Counter-Proliferation Financing (CPF), KYC and sanctions requirements under Nigerian law and CBN Guidelines.
        </p>
        <p>Transactions may be subject to automated and manual screening. Entrusted may:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>Request additional information or documentation</li>
          <li>Place a transaction on hold</li>
          <li>Reject or cancel a transaction</li>
          <li>Restrict or suspend an account</li>
          <li>Report a transaction or activity to the Nigeria Financial Intelligence Unit (NFIU), CBN or other competent authority</li>
        </ul>
        <p>
          Entrusted may not always be able to disclose the specific reason for a restriction where doing so would breach legal or regulatory requirements.
        </p>
      </div>
    ),
  },
  {
    id: "section-14",
    num: "14",
    title: "Customer Responsibilities",
    content: (
      <div className="space-y-4">
        <p>You are responsible for:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>Providing accurate information</li>
          <li>Maintaining account security</li>
          <li>Using the Platform lawfully</li>
          <li>Reviewing transaction details carefully before confirmation</li>
          <li>Keeping your contact information current</li>
          <li>Protecting your authentication credentials</li>
          <li>Promptly reporting suspicious or unauthorised activity</li>
        </ul>
        <p>
          You must not use the Platform in a manner that could damage, disable, overburden or impair the Platform or interfere with any other party&apos;s use of the Services.
        </p>
      </div>
    ),
  },
  {
    id: "section-15",
    num: "15",
    title: "Intellectual Property",
    content: (
      <div className="space-y-4">
        <p>
          All intellectual property rights relating to the Entrusted Platform, including its software, designs, logos, trademarks, content, interfaces, graphics and documentation, belong to Entrusted or its respective licensors.
        </p>
        <p>
          You are granted a limited, non-exclusive, non-transferable, revocable right to use the Platform for its intended purposes. You must not copy, reproduce, modify, reverse engineer, distribute, sell or commercially exploit any part of the Platform without prior written authorisation from Entrusted.
        </p>
      </div>
    ),
  },
  {
    id: "section-16",
    num: "16",
    title: "Privacy and Personal Data",
    content: (
      <div className="space-y-4">
        <p>
          Entrusted collects and processes personal data in accordance with its <Link to="/privacy-policy" className="text-primary-500 font-semibold underline">Privacy Policy</Link> and the Nigeria Data Protection Act 2023. Information may be collected and processed for purposes including account creation, identity verification, transaction processing, fraud prevention, AML/KYC compliance, customer support, regulatory reporting, security and service improvement.
        </p>
        <p>
          By using the Platform, you acknowledge that your information may be processed as described in Entrusted&apos;s Privacy Policy.
        </p>
      </div>
    ),
  },
  {
    id: "section-17",
    num: "17",
    title: "Third-Party Services",
    content: (
      <div className="space-y-4">
        <p>
          Entrusted may integrate with third-party service providers, including payment processors, authorised dealer banks, identity verification providers, technology providers and payout partners. Third-party services may be subject to their own terms and policies. Entrusted will take reasonable steps to work with authorised and appropriate service providers but may not be responsible for failures caused solely by third-party systems beyond Entrusted&apos;s reasonable control.
        </p>
      </div>
    ),
  },
  {
    id: "section-18",
    num: "18",
    title: "Service Availability",
    content: (
      <div className="space-y-4">
        <p>
          Entrusted will make reasonable efforts to keep the Platform available and operational. However, continuous or uninterrupted availability is not guaranteed. The Platform may temporarily become unavailable because of:
        </p>
        <ul className="list-disc pl-5 space-y-2">
          <li>Scheduled maintenance</li>
          <li>Technical failures or cybersecurity incidents</li>
          <li>Network interruptions</li>
          <li>Third-party service failures</li>
          <li>Regulatory requirements or system upgrades</li>
          <li>Events beyond Entrusted&apos;s reasonable control</li>
        </ul>
      </div>
    ),
  },
  {
    id: "section-19",
    num: "19",
    title: "Suspension and Termination",
    content: (
      <div className="space-y-4">
        <p>Entrusted may suspend, restrict or terminate your account where:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>You breach these Terms</li>
          <li>You provide false or misleading information</li>
          <li>Your account is associated with suspected fraud or financial crime</li>
          <li>Your activity presents a compliance or security risk</li>
          <li>Required KYC information is not provided</li>
          <li>Your activity violates applicable law</li>
          <li>A regulatory authority requires action</li>
          <li>Entrusted reasonably believes continued access presents a risk</li>
        </ul>
        <p>
          Where legally permitted, Entrusted may notify you of the reason for suspension or termination. Termination does not affect obligations or liabilities that arose before termination.
        </p>
      </div>
    ),
  },
  {
    id: "section-20",
    num: "20",
    title: "Refunds and Reversals",
    content: (
      <div className="space-y-4">
        <p>
          Refunds, reversals and transaction cancellations will be handled in accordance with applicable laws, regulatory requirements, transaction status and Entrusted&apos;s applicable refund procedures. Where a transaction cannot be completed, Entrusted may initiate a refund subject to applicable conditions and third-party processing requirements.
        </p>
        <p>
          Refunds may be subject to applicable fees or deductions where permitted by law and disclosed to the customer.
        </p>
      </div>
    ),
  },
  {
    id: "section-21",
    num: "21",
    title: "Limitation of Liability",
    content: (
      <div className="space-y-4">
        <p>To the maximum extent permitted by applicable law, Entrusted will not be liable for losses arising from:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>Incorrect information supplied by the customer</li>
          <li>Unauthorised use resulting from the customer&apos;s failure to protect account credentials</li>
          <li>Third-party service interruptions or failures</li>
          <li>Network or telecommunications failures</li>
          <li>Delays caused by regulatory or compliance checks</li>
          <li>Events beyond Entrusted&apos;s reasonable control</li>
          <li>Illegal or unauthorised activities undertaken by the customer</li>
        </ul>
        <p className="bg-primary-50 p-3.5 rounded-xl border border-primary-100 text-sm text-primary-900 font-medium">
          Nothing in these Terms excludes or limits liability that cannot legally be excluded or limited under applicable Nigerian law.
        </p>
      </div>
    ),
  },
  {
    id: "section-22",
    num: "22",
    title: "Indemnity",
    content: (
      <div className="space-y-4">
        <p>
          To the extent permitted by law, you agree to indemnify and hold Entrusted, its officers, employees, agents and authorised service providers harmless against claims, losses, damages, liabilities and reasonable expenses arising from:
        </p>
        <ul className="list-disc pl-5 space-y-2">
          <li>Your violation of these Terms</li>
          <li>Your unlawful use of the Platform</li>
          <li>False or misleading information provided by you</li>
          <li>Your infringement of another person&apos;s rights</li>
        </ul>
      </div>
    ),
  },
  {
    id: "section-23",
    num: "23",
    title: "Changes to These Terms",
    content: (
      <div className="space-y-4">
        <p>
          Entrusted may update these Terms from time to time to reflect changes in applicable laws or regulations, changes to our Services, technology or security measures, or operational requirements. Updated Terms will be made available through the Platform.
        </p>
        <p>
          Where required by law, Entrusted will provide appropriate notice before material changes take effect. Your continued use of the Platform after the effective date of updated Terms constitutes acceptance of the revised Terms.
        </p>
      </div>
    ),
  },
  {
    id: "section-24",
    num: "24",
    title: "Governing Law and Dispute Resolution",
    content: (
      <div className="space-y-4">
        <p>
          These Terms shall be governed by and interpreted in accordance with the laws of the Federal Republic of Nigeria, subject to applicable regulatory requirements of the Central Bank of Nigeria.
        </p>
        <p>
          Any dispute arising from these Terms shall be subject to the jurisdiction of the appropriate courts of Nigeria, unless otherwise required by applicable law or agreed between the parties. Customers are encouraged to first contact Entrusted&apos;s customer support to seek an amicable resolution of any complaint.
        </p>
      </div>
    ),
  },
  {
    id: "section-25",
    num: "25",
    title: "Complaints and Customer Support",
    content: (
      <div className="space-y-4">
        <p>
          Customers may contact Entrusted through the official customer support channels provided on the Platform for:
        </p>
        <ul className="list-disc pl-5 space-y-2">
          <li>Transaction enquiries</li>
          <li>Account issues</li>
          <li>Failed or delayed transactions</li>
          <li>Refund enquiries</li>
          <li>Complaints</li>
          <li>Security concerns</li>
          <li>Other service-related issues</li>
        </ul>
        <p>
          Entrusted will handle complaints in accordance with its applicable complaint-handling procedures and CBN consumer-protection requirements. If a complaint remains unresolved, you may escalate it to the Central Bank of Nigeria or other competent authority in accordance with applicable procedures.
        </p>
      </div>
    ),
  },
  {
    id: "section-26",
    num: "26",
    title: "Contact Information",
    content: (
      <div className="space-y-4">
        <div className="bg-secondary-500 text-white p-6 sm:p-8 rounded-2xl space-y-4 shadow-md">
          <h4 className="font-bold text-2xl text-white">Entrusted Limited</h4>
          <div className="grid sm:grid-cols-2 gap-4 text-sm pt-4 border-t border-white/20 text-white">
            <div>
              <span className="block text-white/80 font-medium">Registered Address:</span>
              <span className="font-semibold text-white">Plot 1014 Fria Close Wuse II Abuja, Nigeria</span>
            </div>
            <div>
              <span className="block text-white/80 font-medium">Official Email:</span>
              <a href="mailto:info@entrusted.com" className="font-semibold underline hover:text-accent-500 text-white">info@entrusted.com</a>
            </div>
            <div>
              <span className="block text-white/80 font-medium">Telephone:</span>
              <span className="font-semibold text-white">+234 708 579 1804</span>
            </div>
            <div>
              <span className="block text-white/80 font-medium">Website:</span>
              <a href="https://entrusted.com" target="_blank" rel="noreferrer" className="font-semibold underline hover:text-accent-500 text-white">www.entrusted.com</a>
            </div>
          </div>
          <div className="pt-4 border-t border-white/20 text-xs text-white/90 font-medium">
            Customer Support: info@entrusted.com / In-App Chat
          </div>
        </div>
      </div>
    ),
  },
  {
    id: "section-27",
    num: "27",
    title: "Acceptance",
    content: (
      <div className="space-y-4">
        <p>
          By clicking &quot;I Agree&quot;, &quot;Accept&quot;, &quot;Register&quot;, &quot;Continue&quot; or by otherwise accessing or using the Entrusted Platform, you confirm that:
        </p>
        <ol className="list-decimal pl-5 space-y-2">
          <li>You have read and understood these Terms of Use</li>
          <li>You agree to be legally bound by these Terms</li>
          <li>The information you provide to Entrusted is accurate and complete</li>
          <li>You will comply with applicable laws and regulations</li>
          <li>You will use the Entrusted Platform only for lawful purposes</li>
        </ol>
      </div>
    ),
  },
];

const TermsOfUse = () => {
  const [searchTerm, setSearchTerm] = useState("");

  const filteredSections = useMemo(() => {
    if (!searchTerm.trim()) return termsSections;
    const term = searchTerm.toLowerCase();
    return termsSections.filter(
      (s) =>
        s.title.toLowerCase().includes(term) ||
        s.num.includes(term)
    );
  }, [searchTerm]);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <Main>
      <Helmet>
        <title>Terms of Use | Entrusted Limited</title>
        <meta
          name="description"
          content="Terms of Use for Entrusted Limited Remittance Application & Platform, compliant with CBN Revised Guidelines (2024) and NDPA 2023."
        />
      </Helmet>

      <div className="bg-[#f6f6f6] min-h-screen pt-[6rem] sm:pt-[8rem] pb-[6rem]">
        {/* Hero Header */}
        <div className="bg-gradient-to-r from-secondary-500 via-blue-900 to-primary-500 text-white py-12 sm:py-16 mb-10 shadow-lg">
          <div className="container px-4 mx-auto">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md px-4 py-1.5 rounded-full text-xs font-semibold tracking-wider text-accent-500 border border-white/20 mb-6">
                <span>Version 2.0 (IMTO Standard)</span>
                <span>•</span>
                <span>CBN IMTO Compliant</span>
              </div>
              <h1 className="text-3xl sm:text-5xl font-bold tracking-tight mb-4 text-white">
                ENTRUSTED LIMITED TERMS OF USE
              </h1>
              <p className="text-lg sm:text-xl font-light text-white/90 mb-6">
                Entrusted Remittance Application / Platform
              </p>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs sm:text-sm text-white max-w-3xl mx-auto pt-6 border-t border-white/15">
                <div>
                  <span className="block font-semibold text-white">Effective Date</span>
                  <span className="text-white/90">4 September 2026</span>
                </div>
                <div>
                  <span className="block font-semibold text-white">Last Updated</span>
                  <span className="text-white/90">4 September 2026</span>
                </div>
                <div>
                  <span className="block font-semibold text-white">Jurisdiction</span>
                  <span className="text-white/90">Nigeria</span>
                </div>
                <div>
                  <span className="block font-semibold text-white">Regulatory Standard</span>
                  <span className="text-white/90">CBN & AML/CFT/CPF</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Regulatory Preamble Box */}
        <div className="container px-4 mx-auto mb-10">
          <div className="max-w-6xl mx-auto bg-white p-6 sm:p-8 rounded-2xl shadow-sm border border-gray-200">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-secondary-500 text-white rounded-xl hidden sm:block">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div className="text-sm sm:text-base text-gray-700 leading-relaxed">
                <strong className="text-gray-900 font-semibold block mb-1">Regulatory Standard & Directives Notice:</strong>
                These Terms of Use are issued in accordance with the <strong>Central Bank of Nigeria (CBN) Revised Guidelines for International Money Transfer Services in Nigeria (2024 and subsequent updates)</strong>, the <strong>Nigeria Data Protection Act 2023</strong>, applicable <strong>AML/CFT/CPF laws</strong>, and other relevant Nigerian legislation.
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="container px-4 mx-auto">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8">
            
            {/* Dark Sleek Sidebar / Navigation */}
            <div className="lg:col-span-4">
              <div className="sticky top-28 bg-secondary-500 text-white p-5 sm:p-6 rounded-2xl shadow-xl border border-primary-500/30 max-h-[80vh] flex flex-col">
                <h3 className="font-bold text-white text-lg mb-3 flex items-center justify-between">
                  <span className="text-white">Table of Contents</span>
                  <span className="text-xs bg-white/20 text-white px-3 py-1 rounded-full font-bold">27 Sections</span>
                </h3>

                {/* Quick Search input */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search section..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full text-xs px-3.5 py-2.5 rounded-xl bg-white/10 text-white placeholder-white/70 border border-white/20 focus:outline-none focus:border-white focus:bg-white/20 transition-all"
                  />
                </div>

                <nav className="space-y-1 text-xs overflow-y-auto pr-1 flex-1">
                  {filteredSections.map((sec) => (
                    <button
                      key={sec.id}
                      onClick={() => scrollToSection(sec.id)}
                      className="w-full text-left py-2.5 px-3 rounded-xl hover:bg-primary-500 text-white transition-all font-medium whitespace-normal break-words leading-normal block border border-transparent hover:border-white/20"
                    >
                      <span className="font-bold text-accent-500 mr-2 text-sm">{sec.num}.</span>
                      <span className="text-white font-medium text-xs sm:text-sm">{sec.title}</span>
                    </button>
                  ))}
                  {filteredSections.length === 0 && (
                    <p className="text-white/60 text-xs py-3 text-center">No matching sections found.</p>
                  )}
                </nav>
              </div>
            </div>

            {/* Content Sections */}
            <div className="lg:col-span-8 space-y-6">
              {filteredSections.map((sec) => (
                <div
                  key={sec.id}
                  id={sec.id}
                  className="bg-white p-6 sm:p-8 rounded-2xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow scroll-mt-28"
                >
                  <div className="flex items-center gap-3 mb-4 pb-3 border-b border-gray-100">
                    <span className="flex items-center justify-center w-8 h-8 rounded-xl bg-secondary-500 text-white font-bold text-sm">
                      {sec.num}
                    </span>
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
                      {sec.title}
                    </h2>
                  </div>
                  <div className="text-gray-700 text-sm sm:text-base leading-relaxed">
                    {sec.content}
                  </div>
                </div>
              ))}

              {/* End of Terms Footer */}
              <div className="text-center py-8 bg-white rounded-2xl border border-gray-200 p-6">
                <p className="text-gray-500 font-medium text-sm">
                  — End of Terms of Use —
                </p>
                <p className="text-xs text-gray-400 mt-2">
                  Version 2.0 | © Entrusted Limited. All Rights Reserved.
                </p>
              </div>
            </div>

          </div>
        </div>
      </div>
    </Main>
  );
};

export default TermsOfUse;
